import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Tab, Tabs, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { faEye } from "@fortawesome/free-regular-svg-icons";
import { request } from "hooks/useOkoApi/useOkoApi";
import { colors, logEducatorDashboard, LogLevel } from "utils";
import { ViewQuestionsModal } from "./ViewQuestionsModal";
import { useHostPageName, useViewedPageTracking } from "hooks/usePageViewTracker";

const MODAL_PANEL_NAME = "All Related Standards";

const TAB_KEYS = ["core", "prerequisites", "extensions"];
const TAB_LABELS = {
  core: "Core",
  prerequisites: "Prerequisites",
  extensions: "Extensions",
};

const flattenLevels = (groups) => {
  const out = [];
  (groups || []).forEach((group) => {
    (group?.standards || []).forEach((s) => {
      if (!s?.code) return;
      out.push({ ...s, level: group.level });
    });
  });
  return out;
};

export const BrowseRelationshipsModal = ({
  isOpen,
  onClose,
  primaryStandardCode,
  primaryStandardName,
  initialTab = "prerequisites",
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [relationships, setRelationships] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewQuestionsTarget, setViewQuestionsTarget] = useState(null);

  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  const hostPageName = useHostPageName();
  const { trackView } = useViewedPageTracking();
  const trackTabView = (key) =>
    trackView(hostPageName, {
      panel: MODAL_PANEL_NAME,
      standard_code: primaryStandardCode,
      tab: TAB_LABELS[key],
    });
  // One view per open, on the tab the "View All" link chose.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (justOpened) trackTabView(initialTab);
  });

  useEffect(() => {
    if (!isOpen || !primaryStandardCode) {
      setRelationships(null);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    request(`standards/${primaryStandardCode}/relationships`, { method: "GET" })
      .then((data) => {
        if (!cancelled) setRelationships(data || null);
      })
      .catch((e) => {
        if (cancelled) return;
        logEducatorDashboard("BrowseRelationshipsModal: relationships fetch failed", {
          level: LogLevel.WARNING,
          event: "load_error",
          standardCode: primaryStandardCode,
          statusCode: e?.status,
        });
        setError(e?.detail || e?.message || "Failed to load standards.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, primaryStandardCode]);

  const tabContent = useMemo(() => {
    if (!relationships) return { core: [], prerequisites: [], extensions: [] };
    return {
      core: (relationships.target || []).map((s) => ({ ...s, level: null })),
      prerequisites: flattenLevels(relationships.prerequisites),
      extensions: flattenLevels(relationships.extensions),
    };
  }, [relationships]);

  const renderTabBody = (tabKey) => {
    if (isLoading) {
      return (
        <div style={{ padding: 32, textAlign: "center" }}>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p style={{ color: colors.milkGlass950, marginTop: 12 }}>Loading…</p>
        </div>
      );
    }
    const items = tabContent[tabKey] || [];
    if (items.length === 0) {
      return (
        <div style={{ color: colors.milkGlass950, fontStyle: "italic", padding: "16px 0" }}>
          No standards available.
        </div>
      );
    }
    return (
      <div>
        {items.map((s) => (
          <div
            key={`${s.code}-${s.level || "core"}`}
            style={{
              alignItems: "flex-start",
              borderBottom: `1px solid ${colors.milkGlass600}`,
              display: "flex",
              gap: 16,
              justifyContent: "space-between",
              padding: "12px 0",
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{
                color: colors.orange600,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}>
                Standard · {s.code}
                {s.level ? ` · Level ${s.level}` : ""}
              </div>
              <div style={{ color: colors.nightlyWoods900, fontSize: 15, fontWeight: 600, marginTop: 2 }}>
                {s.name || s.description || ""}
              </div>
            </div>
            <button
              onClick={() => setViewQuestionsTarget({ code: s.code, name: s.name })}
              style={{
                alignItems: "center",
                background: "transparent",
                border: "none",
                color: colors.blue500,
                cursor: "pointer",
                display: "inline-flex",
                fontSize: 13,
                fontWeight: 600,
                gap: 6,
                padding: 0,
                whiteSpace: "nowrap",
              }}
            >
              <FontAwesomeIcon icon={faEye} />
              View Questions
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Modal show={isOpen} onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: colors.nightlyWoods900, fontSize: 20, fontWeight: 700 }}>
            All Related Standards
            {primaryStandardCode && (
              <div style={{ color: colors.milkGlass950, fontSize: 13, fontWeight: 500, marginTop: 4 }}>
                Standard · {primaryStandardCode}
                {primaryStandardName ? ` · ${primaryStandardName}` : ""}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: 400 }}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Tabs activeKey={activeTab} onSelect={(k) => {
            if (!k) return;
            if (k !== activeTab) trackTabView(k);
            setActiveTab(k);
          }} className="mb-3">
            {TAB_KEYS.map((k) => (
              <Tab key={k} eventKey={k} title={TAB_LABELS[k]}>
                {renderTabBody(k)}
              </Tab>
            ))}
          </Tabs>
        </Modal.Body>
      </Modal>
      <ViewQuestionsModal
        isOpen={Boolean(viewQuestionsTarget)}
        onClose={() => setViewQuestionsTarget(null)}
        standardCode={viewQuestionsTarget?.code}
        standardName={viewQuestionsTarget?.name}
      />
    </>
  );
};
